import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  formatCurrency,
  formatSignedCurrency,
  formatSignedPercent,
} from "../../../../constants/investmentLeague";

const CloseIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const PreviousInvestmentsModal = ({
  open,
  onClose,
  units = [],
  investorUnit = "",
  previousQuarter = "Q2",
}) => {
  const theme = useTheme();
  const t = theme.custom;
  const isDark = theme.palette.mode === "dark";

  // Only show units where previous investment > 0 or marked invested in previous quarter
  const previousInvestments = units.filter(
    (u) =>
      (u.previousQuarterInvestment || 0) > 0 ||
      (u.q2Invested || 0) > 0 ||
      (u.q1Invested || 0) > 0 ||
      u.investedInPreviousQuarter === true
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: t.surface,
          border: `1px solid ${t.border2}`,
          borderRadius: "14px",
          backgroundImage: "none",
          boxShadow: isDark ? "0 20px 40px rgba(0, 0, 0, 0.6)" : "0 10px 30px rgba(3, 81, 77, 0.15)",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: `1px solid ${t.border}`,
          pb: 2,
        }}
      >
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: "1.1rem", color: t.text }}>
            Previous Quarter Investments
          </Typography>
          <Typography sx={{ fontSize: "0.8rem", color: t.text3 }}>
            Holdings in {previousQuarter} for {investorUnit}
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: t.text3, "&:hover": { color: t.text } }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ py: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow
              sx={{
                "& th": {
                  backgroundColor: t.surface2,
                  borderBottom: `1px solid ${t.border2}`,
                  color: t.text3,
                  fontSize: "0.72rem",
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  py: 1,
                },
              }}
            >
              <TableCell>Recipient Unit</TableCell>
              <TableCell>Type</TableCell>
              <TableCell align="right">{previousQuarter} Invested</TableCell>
              <TableCell align="right">{previousQuarter} Return</TableCell>
              <TableCell align="right">{previousQuarter} ROI</TableCell>
              <TableCell align="right">Cumulative Variance</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {previousInvestments.map((row) => {
              const invested = row.previousQuarterInvestment ?? row.q2Invested ?? row.q1Invested ?? 0;
              const ret = row.previousQuarterReturn ?? row.q2Return ?? 0;
              const roi = row.previousQuarterRoi != null ? row.previousQuarterRoi : row.q2Roi;
              const cumVar = row.cumulativeVariance;

              return (
                <TableRow
                  key={row.buUnit || row.unitName}
                  sx={{
                    "& td": {
                      borderBottom: `1px solid ${t.border}`,
                      fontSize: "0.82rem",
                      color: t.text,
                      py: 1.2,
                    },
                  }}
                >
                  <TableCell sx={{ fontWeight: 600, color: t.text }}>{row.buUnit || row.unitName}</TableCell>
                  <TableCell sx={{ color: t.text2 }}>{row.type}</TableCell>
                  <TableCell align="right">{formatCurrency(invested)}</TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      color: ret >= 0 ? (isDark ? "#10B981" : "#2D8A5B") : (isDark ? "#EF4444" : "#DC2626"),
                      fontWeight: 600,
                    }}
                  >
                    {ret !== 0 ? formatSignedCurrency(ret) : "$0"}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      color: roi == null ? t.text3 : roi >= 0 ? (isDark ? "#10B981" : "#2D8A5B") : (isDark ? "#EF4444" : "#DC2626"),
                      fontWeight: 600,
                    }}
                  >
                    {roi != null ? formatSignedPercent(roi) : "-"}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      color: (cumVar || 0) > 0 ? (isDark ? "#10B981" : "#2D8A5B") : (cumVar || 0) < 0 ? (isDark ? "#EF4444" : "#DC2626") : t.text3,
                      fontWeight: 600,
                    }}
                  >
                    {cumVar != null ? formatSignedPercent(cumVar) : "-"}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </DialogContent>

      <DialogActions sx={{ borderTop: `1px solid ${t.border}`, px: 3, py: 1.5 }}>
        <Button
          onClick={onClose}
          sx={{
            color: t.text2,
            "&:hover": { color: t.text },
            textTransform: "none",
            fontSize: "0.85rem",
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PreviousInvestmentsModal;

